export const calendarEn = {
  common: {
    optional: 'optional',
    minutesShort: (minutes: number) => `${minutes} min`,
  },
  view: {
    regionLabel: 'Raid calendar',
    chooseGuildTitle: 'Choose a guild to view its raid calendar.',
    chooseGuildDescription: 'Raids are shared with the selected Discord guild.',
    signInTitle: 'Sign in with Discord to view this guild’s raid calendar.',
    signInDescription: 'Members can view scheduled raids; officers can create, edit, and delete them.',
    scrollHint: 'Swipe horizontally to view all days →',
    loading: 'Loading raids…',
  },
  header: {
    toolbarLabel: 'Calendar toolbar',
    week: 'Week',
    previousWeek: 'Previous week',
    currentWeek: 'Jump to current week',
    today: 'Today',
    nextWeek: 'Next week',
    planRaidLabel: 'Plan a new raid',
    planRaid: '+ Plan raid',
  },
  status: {
    retry: 'Try again',
    dismiss: 'Dismiss',
    viewOnly: 'View-only calendar — only officers and the Discord owner can schedule or edit raids.',
    cloneSource: 'Choose a raid on the calendar to clone its setup into a new scheduled raid.',
    loadError: 'Could not load this week’s raids.',
    scheduleError: 'Could not move this raid to the selected time.',
    openError: 'Could not open this raid.',
  },
  grid: {
    gridLabel: (weekLabel: string) => `Calendar grid for ${weekLabel}`,
    time: 'Time',
    cloneSetupFrom: (raidLabel: string) => `Clone setup from ${raidLabel}`,
    openPlanFor: (raidLabel: string) => `Open plan for ${raidLabel}`,
    actionsFor: (raidName: string) => `Actions for ${raidName}`,
    raidActions: 'Raid actions',
    publish: 'Publish',
    unpublish: 'Unpublish',
    publishRaid: (raidName: string) => `Publish ${raidName}`,
    unpublishRaid: (raidName: string) => `Unpublish ${raidName}`,
    editSchedule: 'Edit schedule',
    editScheduleFor: (raidName: string) => `Edit schedule for ${raidName}`,
  },
  miniMonth: {
    navigatorLabel: (monthLabel: string) => `Month navigator for ${monthLabel}`,
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    chooseCurrent: (monthLabel: string) => `Choose month and year, currently ${monthLabel}`,
    choose: 'Choose month and year',
    previousYear: 'Previous year',
    nextYear: 'Next year',
    year: 'Year',
    monthsInYear: (year: number) => `Months in ${year}`,
    selectedWeek: (dateLabel: string) => `${dateLabel}, selected week`,
  },
  schedule: {
    eyebrow: 'Raid schedule',
    planTitle: 'Plan a raid',
    editTitle: 'Edit raid',
    oldRaidReadOnly: 'This raid is historical. Only the super admin can edit it.',
    closeLabel: 'Close raid schedule',
    name: 'Raid name',
    characterCount: (count: number, max: number) => `${count}/${max} characters`,
    copyFrom: 'Copy setup from',
    currentBoard: 'Start with the current board',
    copyHelp: 'Copies rooms, groups, players, builds, and stages from the selected raid.',
    date: 'Date',
    startTime: 'Start time',
    duration: 'Duration in minutes',
    notes: 'Notes',
    deleteRaid: 'Delete raid',
    cancelRaid: 'Cancel raid',
    close: 'Close',
    saving: 'Saving…',
    save: 'Save schedule',
    invalidName: (max: number) => `Enter a raid name between 1 and ${max} characters.`,
    invalidDateTime: 'Choose a valid date and start time.',
    cancelConfirm: (raidName: string) => `Cancel “${raidName}”?`,
    deleteError: 'Unable to delete this raid. Please try again.',
  },
  recurrence: {
    enabled: 'Make this a recurring raid',
    cadence: 'Cadence',
    occurrences: 'Number of occurrences',
    weekly: 'Repeats every 7 days',
    biweekly: 'Repeats every 14 days',
  },
  selector: {
    toolbarLabel: 'Raid selector',
    raid: 'Raid',
    liveBoardLabel: 'View live board',
    liveBoard: 'Live board',
    scheduledFor: (date: string) => `Scheduled for ${date}`,
    titleScheduled: (raidName: string, date: string) => `${raidName} — scheduled ${date}`,
    ariaRaid: (raidName: string) => `Raid ${raidName}`,
    ariaRaidScheduled: (raidName: string, date: string) => `Raid ${raidName} scheduled ${date}`,
    actionsLabel: 'Raid actions',
    apply: 'Apply to board',
    delete: 'Delete',
  },
  planNavigation: {
    navigationLabel: 'Raid plan navigation',
    currentGvg: 'Current GvG',
    previous: 'Previous raid',
    next: 'Next raid',
    previousUnavailable: 'No previous planned raid',
    nextUnavailable: 'No next planned raid',
    previousAria: (raidName: string) => `Open previous planned raid: ${raidName}`,
    nextAria: (raidName: string) => `Open next planned raid: ${raidName}`,
  },
  stage: {
    toolbarLabel: 'Stage selector',
    stage: 'Stage',
    all: 'All stages',
    loading: 'Loading stage…',
    addLabel: 'Add stage',
    add: '+ Add stage',
    renameLabel: 'Rename active custom stage',
    rename: 'Rename',
    name: 'Custom stage name',
    nameCount: (count: number, max: number) => `${count}/${max}`,
    saveName: 'Save name',
    cancel: 'Cancel',
    renaming: 'Saving…',
    nameRequired: 'Enter a custom stage name.',
    nameTooLong: (max: number) => `Custom stage names can be at most ${max} characters.`,
    renameError: 'Could not rename this custom stage. Please try again.',
    removeLabel: 'Remove active stage',
    remove: 'Remove',
    opening: 'Opening',
    forest: 'Forest',
    towers: 'Towers',
    goose: 'Goose',
    tree: 'Tree',
    custom: 'Custom',
    ariaStage: (stageName: string) => `Stage ${stageName}`,
  },
  stageDiff: {
    eyebrow: 'Stage overrides',
    titleAll: 'Changes from ALL',
    titleCurrent: (stageName: string) => `${stageName} changes from ALL`,
    summary: (playerCount: number, stageCount: number) =>
      `${playerCount} ${playerCount === 1 ? 'player' : 'players'} · ${stageCount} ${stageCount === 1 ? 'stage' : 'stages'}`,
    playerStageSummary: (count: number) => `${count} changed ${count === 1 ? 'stage' : 'stages'}`,
    playerChanges: (playerName: string) => `Stage changes for ${playerName}`,
    emptyAll: 'No stage-specific changes yet.',
    emptyCurrent: 'This stage currently matches ALL.',
    stageLabel: (stageName: string) => `Stage: ${stageName}`,
    move: 'Move',
    moveChange: (source: string, target: string) => `Moved from ${source} to ${target}`,
    build: 'Build',
    buildChange: 'Active source and target build',
    sourceBuild: 'Source build from ALL',
    targetBuild: 'Target build for this stage',
    buildNumber: (number: number) => `Build ${number}`,
    emptyBuild: 'No active build',
    emptyWeapons: 'No weapons',
    buildSnapshot: (number: number, weapons: string) => `Build ${number}: ${weapons}`,
    tags: 'Tags',
    addedTag: (tagName: string) => `Added tag ${tagName}`,
    removedTag: (tagName: string) => `Removed tag ${tagName}`,
    unassigned: 'Unassigned',
    resetCurrent: 'Reset stage changes',
    resetAll: 'Reset all stage changes',
    confirmCurrentTitle: 'Reset this stage?',
    confirmAllTitle: 'Reset every stage?',
    confirmCurrentBody: 'This removes every placement, build, and tag override in the selected stage. ALL is unchanged.',
    confirmAllBody: 'This removes every placement, build, and tag override from all non-ALL stages. ALL is unchanged.',
    cancel: 'Cancel',
    confirm: 'Reset changes',
    resetting: 'Resetting…',
    resetError: 'Could not reset stage changes. Please try again.',
  },
  formatters: {
    unscheduled: 'Unscheduled',
    plannedRaidCount: (date: string, count: number) =>
      `${date}, ${count} planned ${count === 1 ? 'raid' : 'raids'}`,
    raidAria: (
      raidName: string,
      time: string,
      duration: number,
      status: string,
      publication: string,
    ) => `${raidName}, ${time}, ${duration} minutes, ${status}, ${publication}`,
    published: 'published',
    unpublished: 'unpublished',
    statusScheduled: 'scheduled',
    statusInProgress: 'in progress',
    statusCompleted: 'completed',
    statusCancelled: 'cancelled',
    statusOverdue: 'overdue',
  },
}
